import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// TYPES
// ============================================================================

export type DrawerPosition = 'left' | 'right' | 'top' | 'bottom';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

// ============================================================================
// SIZE AND POSITION UTILITIES
// ============================================================================

const sizeStyles: Record<DrawerPosition, Record<DrawerSize, string>> = {
  left: {
    sm: 'w-64',
    md: 'w-80',
    lg: 'w-96',
    xl: 'w-[480px]',
    full: 'w-screen',
  },
  right: {
    sm: 'w-64',
    md: 'w-80',
    lg: 'w-96',
    xl: 'w-[480px]',
    full: 'w-screen',
  },
  top: {
    sm: 'h-32',
    md: 'h-48',
    lg: 'h-64',
    xl: 'h-96',
    full: 'h-screen',
  },
  bottom: {
    sm: 'h-32',
    md: 'h-48',
    lg: 'h-64',
    xl: 'h-96',
    full: 'h-screen',
  },
};

const positionStyles: Record<DrawerPosition, string> = {
  left: 'inset-y-0 left-0',
  right: 'inset-y-0 right-0',
  top: 'inset-x-0 top-0',
  bottom: 'inset-x-0 bottom-0',
};

const translateStyles: Record<DrawerPosition, { open: string; closed: string }> = {
  left: {
    open: 'translate-x-0',
    closed: '-translate-x-full',
  },
  right: {
    open: 'translate-x-0',
    closed: 'translate-x-full',
  },
  top: {
    open: 'translate-y-0',
    closed: '-translate-y-full',
  },
  bottom: {
    open: 'translate-y-0',
    closed: 'translate-y-full',
  },
};

// ============================================================================
// DRAWER CONTEXT
// ============================================================================

interface DrawerContextValue {
  isOpen: boolean;
  onClose: () => void;
}

const DrawerContext = React.createContext<DrawerContextValue | null>(null);

function useDrawerContext() {
  const context = React.useContext(DrawerContext);
  if (!context) {
    throw new Error('Drawer components must be used within a Drawer');
  }
  return context;
}

// ============================================================================
// DRAWER OVERLAY
// ============================================================================

export interface DrawerOverlayProps {
  className?: string;
}

export function DrawerOverlay({ className }: DrawerOverlayProps) {
  const { onClose } = useDrawerContext();

  return (
    <div
      className={clsx(
        'fixed inset-0 z-40 bg-black/50',
        'animate-in fade-in-0 duration-200',
        className
      )}
      aria-hidden="true"
      onClick={onClose}
    />
  );
}

// ============================================================================
// DRAWER CONTENT
// ============================================================================

export interface DrawerContentProps {
  children: React.ReactNode;
  className?: string;
}

export function DrawerContent({ children, className }: DrawerContentProps) {
  return <div className={clsx('flex-1 overflow-auto', className)}>{children}</div>;
}

// ============================================================================
// DRAWER HEADER
// ============================================================================

export interface DrawerHeaderProps {
  children: React.ReactNode;
  showCloseButton?: boolean;
  className?: string;
}

export function DrawerHeader({
  children,
  showCloseButton = true,
  className,
}: DrawerHeaderProps) {
  const { onClose } = useDrawerContext();

  return (
    <div
      className={clsx(
        'flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700',
        className
      )}
    >
      <div className="font-semibold text-gray-900 dark:text-white">{children}</div>
      {showCloseButton && (
        <button
          type="button"
          onClick={onClose}
          className={clsx(
            'p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700',
            'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
          )}
          aria-label="Close drawer"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

// ============================================================================
// DRAWER BODY
// ============================================================================

export interface DrawerBodyProps {
  children: React.ReactNode;
  className?: string;
}

export function DrawerBody({ children, className }: DrawerBodyProps) {
  return (
    <div className={clsx('flex-1 overflow-auto p-4', className)}>{children}</div>
  );
}

// ============================================================================
// DRAWER FOOTER
// ============================================================================

export interface DrawerFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function DrawerFooter({ children, className }: DrawerFooterProps) {
  return (
    <div
      className={clsx(
        'flex items-center justify-end gap-3 px-4 py-3',
        'border-t border-gray-200 dark:border-gray-700',
        'bg-gray-50 dark:bg-gray-800/50',
        className
      )}
    >
      {children}
    </div>
  );
}

// ============================================================================
// DRAWER CLOSE BUTTON
// ============================================================================

export interface DrawerCloseProps {
  children?: React.ReactNode;
  className?: string;
  asChild?: boolean;
}

export function DrawerClose({ children, className, asChild }: DrawerCloseProps) {
  const { onClose } = useDrawerContext();

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<{ onClick?: () => void }>, {
      onClick: onClose,
    });
  }

  return (
    <button
      type="button"
      onClick={onClose}
      className={clsx(
        'px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300',
        'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600',
        'rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        className
      )}
    >
      {children || 'Close'}
    </button>
  );
}

// ============================================================================
// DRAWER
// ============================================================================

export interface DrawerProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  position?: DrawerPosition;
  size?: DrawerSize;
  showOverlay?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  overlayClassName?: string;
}

export function Drawer({
  children,
  isOpen,
  onClose,
  position = 'right',
  size = 'md',
  showOverlay = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className,
  overlayClassName,
}: DrawerProps) {
  const drawerRef = React.useRef<HTMLDivElement>(null);

  // Handle escape key
  React.useEffect(() => {
    if (isOpen && closeOnEscape) {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, closeOnEscape, onClose]);

  // Prevent body scroll when open
  React.useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Focus trap
  React.useEffect(() => {
    if (isOpen && drawerRef.current) {
      const drawer = drawerRef.current;
      const focusableElements = drawer.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTab = (event: KeyboardEvent) => {
        if (event.key !== 'Tab') return;

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTab);
      firstElement?.focus();

      return () => document.removeEventListener('keydown', handleTab);
    }
  }, [isOpen]);

  const contextValue = React.useMemo(
    () => ({ isOpen, onClose }),
    [isOpen, onClose]
  );

  if (!isOpen) return null;

  return (
    <DrawerContext.Provider value={contextValue}>
      <div className="fixed inset-0 z-50 overflow-hidden">
        {showOverlay && (
          <div
            className={clsx(
              'fixed inset-0 bg-black/50',
              'animate-in fade-in-0 duration-200',
              overlayClassName
            )}
            aria-hidden="true"
            onClick={closeOnOverlayClick ? onClose : undefined}
          />
        )}

        <div
          ref={drawerRef}
          className={clsx(
            'fixed flex flex-col bg-white dark:bg-gray-800',
            'shadow-xl transform transition-transform duration-300 ease-out',
            positionStyles[position],
            sizeStyles[position][size],
            translateStyles[position].open,
            position === 'left' || position === 'right' ? 'h-full' : 'w-full',
            className
          )}
          role="dialog"
          aria-modal="true"
          aria-label="Drawer"
        >
          {children}
        </div>
      </div>
    </DrawerContext.Provider>
  );
}

// ============================================================================
// SIMPLE DRAWER (All-in-one component)
// ============================================================================

export interface SimpleDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  position?: DrawerPosition;
  size?: DrawerSize;
  showCloseButton?: boolean;
  showOverlay?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
}

export function SimpleDrawer({
  isOpen,
  onClose,
  title,
  children,
  footer,
  position = 'right',
  size = 'md',
  showCloseButton = true,
  showOverlay = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className,
}: SimpleDrawerProps) {
  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position={position}
      size={size}
      showOverlay={showOverlay}
      closeOnOverlayClick={closeOnOverlayClick}
      closeOnEscape={closeOnEscape}
      className={className}
    >
      {title && (
        <DrawerHeader showCloseButton={showCloseButton}>{title}</DrawerHeader>
      )}
      <DrawerBody>{children}</DrawerBody>
      {footer && <DrawerFooter>{footer}</DrawerFooter>}
    </Drawer>
  );
}

// ============================================================================
// CONFIRMATION DRAWER
// ============================================================================

export interface ConfirmationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  position?: DrawerPosition;
  size?: DrawerSize;
  loading?: boolean;
}

export function ConfirmationDrawer({
  isOpen,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  position = 'right',
  size = 'md',
  loading = false,
}: ConfirmationDrawerProps) {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} position={position} size={size}>
      <DrawerHeader>{title}</DrawerHeader>
      <DrawerBody>{children}</DrawerBody>
      <DrawerFooter>
        <DrawerClose>{cancelLabel}</DrawerClose>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading}
          className={clsx(
            'px-4 py-2 text-sm font-medium text-white rounded-lg',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
            confirmVariant === 'danger'
              ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500'
              : 'bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500',
            loading && 'opacity-50 cursor-not-allowed'
          )}
        >
          {loading ? 'Loading...' : confirmLabel}
        </button>
      </DrawerFooter>
    </Drawer>
  );
}

// ============================================================================
// FORM DRAWER
// ============================================================================

export interface FormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (event: React.FormEvent) => void;
  title: React.ReactNode;
  children: React.ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  position?: DrawerPosition;
  size?: DrawerSize;
  loading?: boolean;
}

export function FormDrawer({
  isOpen,
  onClose,
  onSubmit,
  title,
  children,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  position = 'right',
  size = 'md',
  loading = false,
}: FormDrawerProps) {
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit(event);
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} position={position} size={size}>
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        <DrawerHeader>{title}</DrawerHeader>
        <DrawerBody>{children}</DrawerBody>
        <DrawerFooter>
          <DrawerClose>{cancelLabel}</DrawerClose>
          <button
            type="submit"
            disabled={loading}
            className={clsx(
              'px-4 py-2 text-sm font-medium text-white rounded-lg',
              'bg-blue-600 hover:bg-blue-700',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500',
              loading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {loading ? 'Saving...' : submitLabel}
          </button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
}

// ============================================================================
// USE DRAWER HOOK
// ============================================================================

export interface UseDrawerOptions {
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export interface UseDrawerReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export function useDrawer(options: UseDrawerOptions = {}): UseDrawerReturn {
  const { defaultOpen = false, onOpenChange } = options;
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const open = React.useCallback(() => {
    setIsOpen(true);
    onOpenChange?.(true);
  }, [onOpenChange]);

  const close = React.useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const toggle = React.useCallback(() => {
    setIsOpen((prev) => {
      const newValue = !prev;
      onOpenChange?.(newValue);
      return newValue;
    });
  }, [onOpenChange]);

  return { isOpen, open, close, toggle };
}
